import { useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Globe, Users, Sparkles, Key, FileText, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { api } from '@/lib/api-client';
import { PublicShell } from '@/components/layout/PublicShell';

type PublicPackage = {
  id: string;
  name: string;
  description: string | null;
  price_try_kurus: number | null;
  price_usd_cents: number | null;
  credits: number;
  duration_days: number;
  sort_order: number;
  kind: string;
};

const ADDON_ICONS: Record<string, typeof Sparkles> = {
  api_access: Zap,
  byok: Key,
  long_articles: FileText,
  web_grounding: Sparkles,
  extra_site: Globe,
  extra_editor: Users,
};

export function PricingPage() {
  const [packages, setPackages] = useState<PublicPackage[] | null>(null);

  useEffect(() => {
    api
      .get<{ packages: PublicPackage[] }>('/api/public/packages')
      .then((d) => setPackages(d.packages))
      .catch(() => setPackages([]));
  }, []);

  // Public pricing only previews: we don't let visitors top up directly.
  // 'balance-custom' is the only balance row shown after 0011; render it
  // as an info card so visitors know the top-up exists, then send them
  // to /register to actually set an amount.
  const addons = (packages ?? []).filter((p) => p.kind !== 'balance');
  const presets = [5, 10, 20, 50, 100];

  return (
    <PublicShell>
      <section className="container py-16 text-center space-y-3">
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight">Ödeme & Eklentiler</h1>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
          Sadece ihtiyacın olanı al: bakiyeyi istediğin miktarda topla, özellikleri teker teker ekle.
          Paket zorunluluğu yok.
        </p>
      </section>

      {/* Balance top-up preview */}
      <section className="container pb-12 space-y-6">
        <div className="text-center space-y-1">
          <h2 className="text-2xl font-bold">Bakiye Ekle</h2>
          <p className="text-sm text-muted-foreground">
            Dilediğin kadar USD bakiye yükle — paket zorunluluğu yok, süre kısıtı yok.
          </p>
        </div>
        <Card className="max-w-2xl mx-auto">
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center justify-center gap-2 flex-wrap">
              {presets.map((usd) => (
                <div
                  key={usd}
                  className="rounded-lg border px-4 py-3 text-center min-w-[72px]"
                >
                  <div className="text-lg font-semibold">${usd}</div>
                  <div className="text-[10px] text-muted-foreground uppercase tracking-wide">preset</div>
                </div>
              ))}
              <div className="rounded-lg border border-dashed px-4 py-3 text-center min-w-[96px]">
                <div className="text-lg font-semibold">$—</div>
                <div className="text-[10px] text-muted-foreground uppercase tracking-wide">özel tutar</div>
              </div>
            </div>
            <p className="text-xs text-muted-foreground text-center">
              Yukarıdaki preset'lerden birini seç veya elle yaz (min $1). Sepete ekle, tek seferde öde.
            </p>
            <Button className="w-full" asChild>
              <Link to="/register">Ücretsiz Başla & Bakiye Yükle</Link>
            </Button>
          </CardContent>
        </Card>
      </section>

      {/* Addons */}
      <section className="container pb-16 space-y-6">
        <div className="text-center space-y-1 pt-6 border-t">
          <h2 className="text-2xl font-bold mt-6">Özellik Eklentileri</h2>
          <p className="text-sm text-muted-foreground">
            Her özellik ayrı satın alınır, birbirinden bağımsız süresi vardır. İhtiyacın olmayan bir şey için ödeme yapma.
          </p>
        </div>

        {addons.length > 0 && (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {addons.map((p) => {
              const Icon = ADDON_ICONS[p.kind] ?? Sparkles;
              return (
                <Card key={p.id}>
                  <CardHeader>
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="space-y-0.5">
                        <CardTitle className="text-base">{p.name}</CardTitle>
                        {p.description && (
                          <CardDescription className="text-xs">{p.description}</CardDescription>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-baseline justify-between">
                      <div className="text-2xl font-semibold">
                        {p.price_usd_cents != null ? `$${(p.price_usd_cents / 100).toFixed(2)}` : '—'}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {p.duration_days} gün
                      </div>
                    </div>
                    <Button className="w-full" variant="outline" asChild>
                      <Link to="/register">Satın Al</Link>
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </section>

      <section className="container pb-16 text-center text-sm text-muted-foreground space-y-2">
        <p>Stripe kart + USDT/USDC (Tron/Ethereum/Solana dahil 9 ağ) ödeme desteği.</p>
        <p>Kayıt olunca hediye bakiye verilir. Eklentiler ayrı ayrı alınır, birden fazla alabilirsin.</p>
      </section>
    </PublicShell>
  );
}
